import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { queryKeys } from '@/shared/constants/query-keys'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import { useReservationCommands } from '../hooks/use-reservation-commands'
import type { Reservation } from '../models/reservation-source'

interface DeclineReservationDialogProps {
  reservation: Reservation
  propertyName: string
  children: ReactNode
}

export function DeclineReservationDialog({
  reservation,
  propertyName,
  children
}: DeclineReservationDialogProps) {
  const queryClient = useQueryClient()
  const commands = useReservationCommands()
  const [notes, setNotes] = useState('')
  const [open, setOpen] = useState(false)

  const mutation = useMutation({
    mutationFn: async () => await commands.decline(reservation, notes.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.availability.all })
      toast.success('Solicitud de apartado rechazada')
      setNotes('')
      setOpen(false)
    },
    onError: (error) => {
      handleMutationError(error)
      queryClient.invalidateQueries({ queryKey: queryKeys.availability.all })
    }
  })

  const handleOpenChange = (value: boolean) => {
    if (!mutation.isPending) {
      setOpen(value)
      if (!value) setNotes('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rechazar Solicitud de Apartado</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Estás por rechazar la solicitud de apartado para <strong>{propertyName}</strong>. Ingresá
          el motivo del rechazo.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="decline-notes">
            Motivo del rechazo <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="decline-notes"
            placeholder="Ingresá el motivo del rechazo..."
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value)
            }}
            rows={3}
            disabled={mutation.isPending}
          />
          {notes.trim().length === 0 && notes.length > 0 && (
            <span className="text-xs text-destructive">El motivo del rechazo es obligatorio</span>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => {
              mutation.mutate()
            }}
            disabled={mutation.isPending || notes.trim().length === 0}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Rechazar Solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
